import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Agent } from '@trendbuild/trend-gpt-maker-api';
import { AbstractComponent, Attendance, AttendanceService, Integration, IntegrationService, IntegrationTypeEnum, OpenAIIntegration, GPTMakerIntegration, OpenAIAssistant } from 'lib-trend-core';
import { map, takeUntil, switchMap, of } from 'rxjs';

@Component({
    selector: 'service-ai-assistant-activator-selector',
    // templateUrl: './ai-assistant-activator-selector.component.html',
    templateUrl: './ai-assistant-activator-selector.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AiAssistantActivatorSelectorComponent extends AbstractComponent implements OnInit {

  assistants: OpenAIAssistant[] = [];
  filteredAssistants: any[] = [];
  integration: Integration;

  openaiIntegration: Integration;
  gptMakerIntegration: Integration;
  filteredAgents: Agent[] = [];

  constructor(
    @Inject(IntegrationService) private integrationService: IntegrationService,
    public attendanceService: AttendanceService,
    public dialogRef: MatDialogRef<AiAssistantActivatorSelectorComponent>,
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public attendance: Attendance,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      activeIAModule: [this.attendance?.metadata?.integrations.ai?.actived || false],
      assistant: [this.attendance?.metadata?.integrations.ai?.openai?.assistant || this.attendance?.metadata?.integrations.ai?.gpt_maker?.assistant || null],
      integration: [this.attendance?.metadata?.integrations.ai?.integration || null]
    });
    this.getIntegrationById();
  }

  getIntegrationById(): void {
    this.integrationService.getList()
      .pipe(
        takeUntil(this.destroy$),
        map((integrations: Integration[]) =>
          integrations.map(integration => {
            if (integration.type === IntegrationTypeEnum.OPENAI) {
              const openAIMetadata = integration.metadata as OpenAIIntegration;
              const filteredAssistants = openAIMetadata.assistants?.filter(assistant =>
                assistant.channels.some(channel => channel._id === this.attendance.channel._id)
              ) || [];
              return {
                ...integration,
                metadata: {
                  ...openAIMetadata,
                  assistants: filteredAssistants,
                }
              };
            } else if (integration.type === IntegrationTypeEnum.GPT_MAKER) {
              const trendGPTMetadata = integration.metadata as GPTMakerIntegration;
              const filteredAgents = trendGPTMetadata.agents?.filter(agent =>
                agent.channels.some(channel => channel._id === this.attendance.channel._id)
              ) || [];
              return {
                ...integration,
                metadata: {
                  ...trendGPTMetadata,
                  agents: filteredAgents,
                }
              };
            }
            return integration;
          })
        )
      )
      .subscribe({
        next: (integrations: Integration[]) => {
          this.openaiIntegration = integrations.find(integration =>
            integration.type === IntegrationTypeEnum.OPENAI &&
            (integration.metadata as OpenAIIntegration).assistants?.length
          ) || null;

          this.gptMakerIntegration = integrations.find(integration =>
            integration.type === IntegrationTypeEnum.GPT_MAKER &&
            (integration.metadata as GPTMakerIntegration).agents?.length
          ) || null;

          this.filteredAssistants = this.openaiIntegration
            ? (this.openaiIntegration.metadata as OpenAIIntegration).assistants || []
            : [];

          this.filteredAgents = this.gptMakerIntegration
            ? (this.gptMakerIntegration.metadata as GPTMakerIntegration).agents || []
            : [];
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  findIdIntegrationByAssistantId(assistantId: string): string | null {
    if (this.openaiIntegration) {
      const assistants = (this.openaiIntegration.metadata as OpenAIIntegration).assistants || [];
      const foundAssistant = assistants.find(a => a.id === assistantId);
      if (foundAssistant) return this.openaiIntegration._id;
    }

    if (this.gptMakerIntegration) {
      const agents = (this.gptMakerIntegration.metadata as GPTMakerIntegration).agents || [];
      const foundAgent = agents.find(a => a.id === assistantId);
      if (foundAgent) return this.gptMakerIntegration._id;
    }

    return null;
  }


  onSelectAssistant(selectedAssistantId: MatSelectChange): void {
    const integrationId = this.findIdIntegrationByAssistantId(selectedAssistantId.value);
    this.formGroup.get('integration').setValue(integrationId);
  }

  confirm(): void {
    if (!this.formGroup.value.assistant) {
      this.alertService.error(this.t('attendance.aiAssistant.selectAssistant'));
      return;
    }
    if (this.formGroup.valid) {
      this.dialogRef.close({
        ...this.formGroup.value,
        activeIAModule: true
      });
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
